import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MapPin, Eye, EyeOff, AlertTriangle, X } from 'lucide-react';

const HIDE_KEY = 'pv2_hide_jobs_map';

const STATUS_COLORS = {
  Served: '#4ade80',
  Active: '#60a5fa',
  'Non-Served': '#f59e0b',
  Canceled: '#9ca3af',
};
const colorFor = (s) => STATUS_COLORS[s] || '#60a5fa';

// Optimized raster marker icons (image icons let Google batch-render hundreds
// of pins far faster than per-marker vector Symbols — big pan/zoom speedup).
const _iconCache = {};
const circleIcon = (color) => {
  if (_iconCache[color]) return _iconCache[color];
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='18' height='18'><circle cx='9' cy='9' r='6' fill='${color}' stroke='#0a0a0a' stroke-width='2'/></svg>`;
  _iconCache[color] = {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(18, 18),
    anchor: new window.google.maps.Point(9, 9),
  };
  return _iconCache[color];
};

// Buckets mirror the dashboard cards exactly (backend sends these values).
const catFor = (s) => (['Served', 'Active', 'Non-Served', 'Canceled'].includes(s) ? s : 'Active');
const CAT_META = [
  { key: 'Active', color: '#60a5fa' },
  { key: 'Served', color: '#4ade80' },
  { key: 'Non-Served', color: '#f59e0b' },
  { key: 'Canceled', color: '#9ca3af' },
];
const CATS_KEY = 'pv2_map_hidden_cats';

// Dark editorial map style to match the portal.
const DARK_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#0f0f0f' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0a0a0a' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#6b6b6b' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#2a2a2a' }] },
  { featureType: 'administrative.province', elementType: 'geometry.stroke', stylers: [{ color: '#3a2020' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1c1c1c' }] },
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#070707' }] },
];

const ClientJobsMap = () => {
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const mapEl = useRef(null);
  const mapObj = useRef(null);
  const markers = useRef([]);
  const overlayRef = useRef(null);
  const selectedRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [selected, setSelected] = useState(null);
  const [popupPos, setPopupPos] = useState(null);
  const [hiddenCats, setHiddenCats] = useState(() => {
    try { const raw = localStorage.getItem(CATS_KEY); return raw ? new Set(JSON.parse(raw)) : new Set(); } catch { return new Set(); }
  });
  const [unmapped, setUnmapped] = useState([]);
  const [showUnmapped, setShowUnmapped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem(HIDE_KEY) === '1'; } catch { return false; }
  });

  const toggleHidden = () => {
    setSelected(null);
    setHidden((prev) => {
      const next = !prev;
      try { localStorage.setItem(HIDE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rPts, rUn] = await Promise.all([
          authFetch('/api/portal/map/jobs'),
          authFetch('/api/portal/map/jobs/unmapped'),
        ]);
        if (rPts.ok && !cancelled) setPoints((await rPts.json()).points || []);
        if (rUn.ok && !cancelled) setUnmapped((await rUn.json()).jobs || []);
      } catch { /* silent — map is a non-critical enhancement */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [authFetch]);

  // Project the selected job's lat/lng to a pixel inside the map so the popup
  // can float attached to its pin and follow along on pan/zoom.
  const updatePopupPos = useCallback(() => {
    const ov = overlayRef.current;
    const sel = selectedRef.current;
    if (!ov || !sel || !ov.getProjection) return;
    const proj = ov.getProjection();
    if (!proj) return;
    const px = proj.fromLatLngToContainerPixel(new window.google.maps.LatLng(sel.lat, sel.lng));
    if (px) setPopupPos({ x: px.x, y: px.y });
  }, []);

  useEffect(() => { selectedRef.current = selected; updatePopupPos(); }, [selected, updatePopupPos]);

  // Show/hide markers instantly when a status chip is toggled (no refetch).
  useEffect(() => {
    markers.current.forEach((m) => { if (m._cat != null) m.setVisible(!hiddenCats.has(m._cat)); });
    try { localStorage.setItem(CATS_KEY, JSON.stringify([...hiddenCats])); } catch { /* ignore */ }
  }, [hiddenCats, points]);

  const toggleCat = (key) => {
    setHiddenCats((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
        // Close an open popup if its pin is being hidden (no orphaned card).
        if (selectedRef.current && catFor(selectedRef.current.status) === key) setSelected(null);
      }
      return next;
    });
  };

  const initMap = useCallback(() => {
    if (!mapEl.current || !window.google?.maps || mapObj.current) return;
    mapObj.current = new window.google.maps.Map(mapEl.current, {
      center: { lat: 31.9686, lng: -99.9018 }, // Texas
      zoom: 6,
      styles: DARK_STYLE,
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: 'cooperative',
      backgroundColor: '#0a0a0a',
    });
    // Clicking empty map space closes any open job popup.
    mapObj.current.addListener('click', () => setSelected(null));
    // Hidden overlay purely to expose a pixel projection for the floating popup.
    const overlay = new window.google.maps.OverlayView();
    overlay.onAdd = () => {}; overlay.draw = () => {}; overlay.onRemove = () => {};
    overlay.setMap(mapObj.current);
    overlayRef.current = overlay;
    ['bounds_changed', 'center_changed', 'zoom_changed', 'drag', 'idle'].forEach((ev) =>
      mapObj.current.addListener(ev, updatePopupPos)
    );
  }, [updatePopupPos]);

  useEffect(() => {
    if (points.length === 0 || hidden) {
      // When hidden, drop the map instance so it re-initializes cleanly on show.
      if (hidden && mapObj.current) {
        markers.current.forEach((m) => m.setMap(null));
        markers.current = [];
        mapObj.current = null;
      }
      return undefined;
    }
    let timer;
    const draw = () => {
      if (!window.google?.maps) { timer = setTimeout(draw, 150); return; }
      initMap();
      if (!mapObj.current) { timer = setTimeout(draw, 150); return; }
      markers.current.forEach((m) => m.setMap(null));
      markers.current = [];
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((p) => {
        const pos = { lat: p.lat, lng: p.lng };
        const marker = new window.google.maps.Marker({
          position: pos,
          map: mapObj.current,
          title: `#${p.job_number} — ${p.recipient}`,
          opacity: p.archived ? 0.55 : 1,
          optimized: true,
          icon: circleIcon(colorFor(p.status)),
        });
        marker._cat = catFor(p.status);
        marker.setVisible(!hiddenCats.has(marker._cat));
        // Toggle a React-controlled popup (Google's InfoWindow is unreadable
        // once the portal's global dark CSS flattens its white bubble).
        marker.addListener('click', () => {
          setSelected((prev) => (prev && prev.id === p.id ? null : p));
        });
        markers.current.push(marker);
        bounds.extend(pos);
      });
      if (points.length === 1) {
        mapObj.current.setCenter(bounds.getCenter());
        mapObj.current.setZoom(11);
      } else if (points.length > 8) {
        // Smarter fit: use the 5th–95th percentile of lat/lng so a few
        // out-of-state outlier pins don't force a whole-hemisphere zoom-out.
        const lats = points.map((p) => p.lat).sort((a, b) => a - b);
        const lngs = points.map((p) => p.lng).sort((a, b) => a - b);
        const q = (arr, f) => arr[Math.max(0, Math.min(arr.length - 1, Math.floor((arr.length - 1) * f)))];
        const core = new window.google.maps.LatLngBounds();
        core.extend({ lat: q(lats, 0.05), lng: q(lngs, 0.05) });
        core.extend({ lat: q(lats, 0.95), lng: q(lngs, 0.95) });
        mapObj.current.fitBounds(core, 50);
      } else {
        mapObj.current.fitBounds(bounds, 60);
      }
    };
    draw();
    return () => clearTimeout(timer);
  }, [points, initMap, navigate, hidden]);

  if (loading || (points.length === 0 && unmapped.length === 0)) return null; // stay subtle — no empty map

  if (hidden) {
    return (
      <section className="pv2-card" style={{ marginBottom: 24, padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }} data-testid="pv2-client-jobs-map-hidden">
        <span className="pv2-card-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, opacity: 0.7 }}>
          <MapPin size={14} strokeWidth={1.5} color="var(--pv2-text-subtle)" /> Your Jobs Map
        </span>
        <button onClick={toggleHidden} className="pv2-mono-sm" data-testid="pv2-show-map"
          style={{ background: 'transparent', border: '1px solid var(--pv2-ink-line)', padding: '6px 12px', color: 'var(--pv2-text-muted)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Eye size={12} strokeWidth={1.5} /> Show map
        </button>
      </section>
    );
  }

  return (
    <section className="pv2-card" style={{ marginBottom: 24, overflow: 'hidden' }} data-testid="pv2-client-jobs-map">
      <div className="pv2-card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <h2 className="pv2-card-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <MapPin size={14} strokeWidth={1.5} color="var(--pv2-red-bright)" /> Your Jobs Map
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {CAT_META.map((c) => {
              const count = points.filter((p) => catFor(p.status) === c.key).length;
              if (count === 0) return null;
              const off = hiddenCats.has(c.key);
              return (
                <button key={c.key} onClick={() => toggleCat(c.key)}
                  data-testid={`pv2-legend-${c.key.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  className="pv2-mono-sm"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: off ? 'var(--pv2-text-subtle)' : 'var(--pv2-text-muted)', opacity: off ? 0.4 : 1, textDecoration: off ? 'line-through' : 'none' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.color, display: 'inline-block', flexShrink: 0 }} />
                  {c.key} {count}
                </button>
              );
            })}
          </div>
          {unmapped.length > 0 && (
            <button onClick={() => setShowUnmapped((v) => !v)} className="pv2-mono-sm" data-testid="pv2-unmapped-badge"
              title="Jobs with no physical address (social media, text, email or phone) that can't be shown on the map"
              style={{ background: showUnmapped ? 'rgba(251,191,36,0.22)' : 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.4)', padding: '5px 10px', color: '#fbbf24', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={12} strokeWidth={1.5} /> {unmapped.length} not on map
            </button>
          )}
          <button onClick={toggleHidden} className="pv2-mono-sm" data-testid="pv2-hide-map"
            style={{ background: 'transparent', border: '1px solid var(--pv2-ink-line)', padding: '5px 10px', color: 'var(--pv2-text-muted)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <EyeOff size={12} strokeWidth={1.5} /> Hide
          </button>
        </div>
      </div>
      {points.length > 0 && (
        <div style={{ position: 'relative' }}>
          <div ref={mapEl} data-testid="pv2-client-map-canvas" style={{ width: '100%', height: 340, background: 'var(--pv2-ink)' }} />
          {selected && popupPos && (() => {
            const placeBelow = popupPos.y < 160;
            return (
            <div data-testid="pv2-client-job-popup"
              style={{ position: 'absolute', left: popupPos.x, top: popupPos.y, width: 244, zIndex: 6,
                transform: placeBelow ? 'translate(-50%, 16px)' : 'translate(-50%, calc(-100% - 16px))' }}>
              <div style={{ position: 'relative', background: '#141414', border: '1px solid #2c2c2c', borderRadius: 4, boxShadow: '0 10px 30px rgba(0,0,0,0.6)', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid #2c2c2c' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: colorFor(selected.status), flexShrink: 0 }} />
                    <span data-testid="pv2-client-popup-name" style={{ fontSize: 13, fontWeight: 700, color: '#f5f5f5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.recipient}</span>
                  </span>
                  <span onClick={() => setSelected(null)} data-testid="pv2-client-popup-close" role="button" tabIndex={0} aria-label="Close"
                    ref={(el) => { if (el) el.style.setProperty('border-radius', '50%', 'important'); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelected(null); }}
                    style={{ flexShrink: 0, width: 24, height: 24, borderRadius: '50%', background: '#ed1c24', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginLeft: 8 }}>
                    <X size={13} strokeWidth={3} />
                  </span>
                </div>
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ fontSize: 11, color: '#9a9a9a', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em' }}>#{selected.job_number} · {selected.status}</div>
                  {selected.city && <div style={{ fontSize: 12, color: '#cfcfcf', marginTop: 4 }}>{selected.city}</div>}
                  <button onClick={() => navigate(`/portal/jobs/${selected.id}`)} data-testid="pv2-client-popup-open"
                    style={{ marginTop: 10, width: '100%', background: 'transparent', border: '1px solid #ed1c24', color: '#ff6b6f', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '7px 0', cursor: 'pointer', borderRadius: 3 }}>
                    Open job →
                  </button>
                </div>
              </div>
              {/* Connector pointing at the pin */}
              <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0,
                ...(placeBelow
                  ? { top: -7, borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderBottom: '7px solid #2c2c2c' }
                  : { bottom: -7, borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderTop: '7px solid #2c2c2c' }) }} />
            </div>
            );
          })()}
        </div>
      )}
      {showUnmapped && unmapped.length > 0 && (
        <div data-testid="pv2-unmapped-panel" style={{ borderTop: '1px solid var(--pv2-ink-line)', maxHeight: 260, overflowY: 'auto' }}>
          <div style={{ padding: '10px 20px', fontSize: 11, color: 'var(--pv2-text-subtle)' }}>
            These jobs have no physical address (social media, text, email or phone), so they can't be shown on the map.
          </div>
          {unmapped.map((j) => (
            <div key={j.id} data-testid="pv2-unmapped-row" onClick={() => navigate(`/portal/jobs/${j.id}`)}
              style={{ padding: '10px 20px', borderTop: '1px solid var(--pv2-ink-line)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pv2-text-muted)' }}>#{j.job_number} · {j.recipient}</div>
                <div style={{ fontSize: 11, color: 'var(--pv2-text-subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.address}</div>
              </div>
              <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#fbbf24', background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)', padding: '3px 8px' }}>{j.reason}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ClientJobsMap;
